class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    window.CervisomGiftCart?.schedule();
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/* CERVISOM: one server-discounted gift; never display an unpaid gift as free. */
(() => {
  if (window.CervisomGiftCart) return;
  const eligible = new Set([16134907134332,16134704923004,16134989676924,16116037517692,16117264810364,16117306065276]);
  let running = null, again = false, timer, suppressed = false, failedSignature = '', lastMessage = '';
  const root = () => document.querySelector('cart-drawer');
  const config = () => document.querySelector('[data-cv-gift]');
  const owned = item => item.properties && item.properties._cervisom_gift === '80eur';
  const paid = cart => cart.items.reduce((sum,item) => sum + (eligible.has(item.product_id) ? item.final_line_price : 0),0);
  const signature = cart => JSON.stringify(cart.items.filter(item=>!owned(item)).map(item=>[item.key,item.quantity,item.final_line_price]));
  async function request(url, body) {
    const response = await fetch(url, body ? {method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify(body)} : {headers:{'Accept':'application/json'},cache:'no-store'});
    const data = await response.json();
    if (!response.ok || data.errors) throw new Error(typeof data.description === 'string' ? data.description : 'Le panier n’a pas pu être actualisé. Réessayez.');
    return data;
  }
  function busy(value) {
    root()?.classList.toggle('cv-gift-busy',value);
    const checkout=document.getElementById('CartDrawer-Checkout');
    if(checkout){ checkout.disabled=value || !!root()?.classList.contains('is-empty'); checkout.setAttribute('aria-busy',String(value)); }
  }
  function message(text) {
    lastMessage=text;
    const el=document.querySelector('[data-cv-gift-error]');
    if(el) el.textContent=text;
  }
  async function paint(cart) {
    const rendered=await request(routes.cart_update_url+'.js',{updates:{},sections:['cart-drawer','cart-icon-bubble'],sections_url:routes.root_url || '/'});
    const html=rendered.sections?.['cart-drawer'];
    if(!html || html.includes('Liquid error'))throw new Error('Shopify ne peut pas afficher le panier pour le moment. Réessayez dans quelques instants.');
    const doc=new DOMParser().parseFromString(html,'text/html');
    const bubbleHtml=rendered.sections?.['cart-icon-bubble'];
    if(bubbleHtml){const wrapper=doc.createElement('div');wrapper.id='cart-icon-bubble';wrapper.innerHTML=new DOMParser().parseFromString(bubbleHtml,'text/html').querySelector('.shopify-section')?.innerHTML || bubbleHtml;doc.body.append(wrapper);}
    const drawer=root(); if(!drawer)return;
    const next=doc.querySelector('#CartDrawer');
    if(!next)throw new Error('Impossible de charger le panier actualisé.');
    const scroll=drawer.querySelector('.cv-cart-scroll')?.scrollTop || 0;
    const wasOpen=drawer.classList.contains('active');
    const focusId=drawer.contains(document.activeElement) ? document.activeElement.id : '';
    document.getElementById('CartDrawer').innerHTML=next.innerHTML;
    drawer.classList.toggle('is-empty',cart.item_count===0);
    drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click',()=>drawer.close());
    const bubble=document.getElementById('cart-icon-bubble'), nextBubble=doc.getElementById('cart-icon-bubble');
    if(bubble && nextBubble)bubble.innerHTML=nextBubble.innerHTML;
    const scroller=drawer.querySelector('.cv-cart-scroll'); if(scroller)scroller.scrollTop=scroll;
    if(wasOpen){const container=cart.item_count ? drawer.querySelector('.drawer__inner') : drawer.querySelector('.drawer__inner-empty'); if(container)trapFocus(container, (focusId && document.getElementById(focusId)) || container);}
    if(lastMessage)message(lastMessage);
  }
  async function reconcile() {
    let cfg=config(); if(!cfg)return;
    busy(true);
    try {
      let cart=await request(routes.cart_url+'.js');
      if(cart.currency!=='EUR')return cart;
      if(!Number(cfg.dataset.variant)||!Number(cfg.dataset.product)){await paint(cart);cfg=config();}
      const variant=Number(cfg?.dataset.variant), product=Number(cfg?.dataset.product);
      if(!variant||!product)throw new Error('Le cadeau est temporairement indisponible. Veuillez réessayer.');
      let changed=false;
      const sig=signature(cart), amount=paid(cart);
      if(amount<10000)suppressed=false;
      const gifts=cart.items.filter(owned);
      const manualMask=cart.items.some(item=>item.product_id===product&&!owned(item));
      const shouldGift=amount>=10000 && cfg.dataset.available==='true' && !suppressed && !manualMask;
      for(let index=0; index<gifts.length; index++){
        const item=gifts[index], quantity=shouldGift&&index===0 ? 1 : 0;
        if(item.quantity!==quantity){cart=await request(routes.cart_change_url+'.js',{id:item.key,quantity});changed=true;}
      }
      if(shouldGift && !cart.items.some(owned) && failedSignature!==sig){
        try{
          await request(routes.cart_add_url+'.js',{items:[{id:variant,quantity:1,properties:{_cervisom_gift:'80eur'}}]});
          cart=await request(routes.cart_url+'.js'); changed=true;
        }catch(error){failedSignature=sig;message('Le cadeau n’a pas pu être ajouté. '+error.message);}
      }
      // Shopify is the authority for pricing. If another offer prevents the gift,
      // remove only the automatically added line so it can never be charged.
      for(const item of cart.items.filter(owned)){
        if(item.final_line_price!==0){cart=await request(routes.cart_change_url+'.js',{id:item.key,quantity:0});changed=true;failedSignature=sig;message('Votre réduction actuelle ne permet pas d’offrir le masque. Aucun cadeau payant n’a été ajouté.');}
      }
      if(failedSignature!==sig){failedSignature='';message('');}
      if(cart.items.some(item=>item.product_id===product&&item.final_line_price===0))message('');
      await paint(cart);
      if(suppressed && amount>=10000){const status=document.querySelector('[data-cv-gift-status]');if(status)status.textContent='Vous avez retiré le cadeau de ce panier.';}
      if(changed)publish(PUB_SUB_EVENTS.cartUpdate,{source:'cervisom-gift',cartData:cart});
      return cart;
    }catch(error){message(error.message || 'Impossible de vérifier le cadeau. Réessayez avant de commander.');throw error;}
    finally{busy(false);}
  }
  function sync(){
    if(running){again=true;return running;}
    running=(async()=>{let cart;do{again=false;cart=await reconcile();}while(again);return cart;})().finally(()=>{running=null;});
    return running;
  }
  function schedule(){clearTimeout(timer);timer=setTimeout(()=>sync().catch(()=>{}),100);}
  window.CervisomGiftCart={sync,schedule};
  subscribe(PUB_SUB_EVENTS.cartUpdate,event=>{if(event.source!=='cervisom-gift')schedule();});
  document.addEventListener('click',event=>{
    if(event.target.closest('.cv-gift-item cart-remove-button'))suppressed=true;
  },true);
  document.addEventListener('submit', async event=>{
    if(event.submitter?.id!=='CartDrawer-Checkout')return;
    event.preventDefault();event.stopImmediatePropagation();
    try{await sync();const checkout=document.getElementById('CartDrawer-Checkout');if(checkout&&!checkout.disabled){const form=document.getElementById('CartDrawer-Form');const input=document.createElement('input');input.type='hidden';input.name='checkout';input.value='';form.append(input);HTMLFormElement.prototype.submit.call(form);}}
    catch(error){message('Vérification du panier impossible. Veuillez réessayer.');}
  },true);
  window.addEventListener('pageshow',schedule);
  document.addEventListener('visibilitychange',()=>{if(!document.hidden)schedule();});
  schedule();
})();
